// ============================================================================
// The dialysis chemical stock card on a building's Supplies tab. 2026-09-23.
//
// Three shapes, decided by DATA (decision 7), never by a name:
//   * an in-scope dialysis clinic  → this card, above the existing chemicals panel;
//   * a dialysis clinic whose customer supplies its own chemicals → ONE line;
//   * every other building         → nothing at all.
//
// ⛔ TCSD-0271: every hook is called unconditionally at the top, before any
// early return, so the hook count never changes between renders.
// ============================================================================

import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  fetchClinicStock,
  fetchCorrections,
  fetchCustomerSupplied,
  fetchDeliveryCosts,
  fetchMovements,
  fetchPeopleNames,
  fetchPhasingOut,
  fetchProducts,
  fetchStockOffices,
  type ClinicStockRow,
  type CorrectionRow,
  type MovementRow,
} from './api';
import {
  cleanerOf,
  fmtDay,
  fmtDays,
  fmtMoney,
  fmtQty,
  fmtRateMonth,
  isNum,
  movementPhrase,
  needsDataLabel,
  rateSourceLabel,
  whyThisNumber,
} from './format';
import { useStockLoad } from './use-stock-data';
import { LogDeliveryWindow } from './log-delivery-window';
import { UpdateCountWindow } from './update-count-window';
import { CorrectEntryWindow, type CorrectionTarget } from './correct-entry-window';
import { StockSettingWindow, type SettingTarget } from './stock-setting-window';
import { PickupWindow } from './move-stock-windows';

const BTN =
  'rounded border border-tcs-border bg-tcs-raised px-2.5 py-1 text-xs font-medium text-tcs-text hover:bg-tcs-surface';
const BTN_PRIMARY =
  'rounded border border-tcs-accent bg-tcs-accent-ink px-2.5 py-1 text-xs font-medium text-tcs-accent-on hover:opacity-90';

export function DialysisStockCard({ facilityId }: { facilityId: string }) {
  const scope = useStockLoad(() => fetchCustomerSupplied(facilityId), [facilityId], {
    dialysis: false,
    customerSupplied: false,
  });
  const stock = useStockLoad(() => fetchClinicStock({ facilityId }), [facilityId], [] as ClinicStockRow[]);
  const all = useStockLoad(() => fetchClinicStock(), [], [] as ClinicStockRow[]);
  const refs = useStockLoad(
    async () => {
      const [offices, products] = await Promise.all([fetchStockOffices(), fetchProducts()]);
      return { offices, products };
    },
    [],
    { offices: [], products: [] } as { offices: Awaited<ReturnType<typeof fetchStockOffices>>; products: Awaited<ReturnType<typeof fetchProducts>> },
  );
  const history = useStockLoad(
    async () => {
      const moves = await fetchMovements({ facilityId });
      const corrections = await fetchCorrections(moves.filter((m) => m.latest_correction_id).map((m) => m.movement_id ?? ''));
      const names = await fetchPeopleNames([...moves.map((m) => m.recorded_by_eid), ...corrections.map((c) => c.recorded_by_eid)]);
      return { moves, corrections, names };
    },
    [facilityId],
    { moves: [] as MovementRow[], corrections: [] as CorrectionRow[], names: {} as Record<string, { name: string; role: string | null }> },
  );
  const costs = useStockLoad(() => fetchDeliveryCosts(), [], [] as Awaited<ReturnType<typeof fetchDeliveryCosts>>);
  const phasing = useStockLoad(() => fetchPhasingOut(facilityId), [facilityId], [] as Array<{ fc_id: string; name: string }>);

  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [countOpen, setCountOpen] = useState(false);
  const [pickupOpen, setPickupOpen] = useState(false);
  const [correction, setCorrection] = useState<CorrectionTarget | null>(null);
  const [setting, setSettingTarget] = useState<SettingTarget | null>(null);
  const [why, setWhy] = useState<Record<string, boolean>>({});

  const rows = stock.data;
  const first = rows[0];
  const officeName = useMemo(() => {
    const m: Record<string, string> = {};
    for (const o of refs.data.offices) m[o.office_id] = o.office_name ?? o.office_id;
    return m;
  }, [refs.data.offices]);

  const myCosts = useMemo(() => costs.data.filter((c) => c.facility_id === facilityId), [costs.data, facilityId]);
  const clinicsWithTwoCounts = useMemo(
    () => new Set(all.data.filter((r) => (r.n_obs ?? 0) >= 1).map((r) => r.facility_id)).size,
    [all.data],
  );

  // ---- the three shapes -----------------------------------------------------
  if (scope.loading || stock.loading) return null;
  if (!scope.data.dialysis) return null;
  if (scope.data.customerSupplied) {
    return (
      <p className="rounded-xl border border-tcs-border bg-tcs-bg px-4 py-3 text-sm text-tcs-text-secondary">
        Customer supplies its own chemicals — not tracked.
      </p>
    );
  }
  if (stock.error) {
    return (
      <p className="rounded-xl border border-st-danger-border bg-st-danger-bg px-4 py-3 text-sm text-st-danger-fg">
        Could not load this clinic’s chemical stock: {stock.error}
      </p>
    );
  }
  if (!first) return null;

  const nd = needsDataLabel(first);
  const cleanerless = first.display_name === first.facility_name;
  const firstDelivery = rows.every((r) => (r.deliveries ?? 0) === 0);
  const startPlus7 = first.start_date ? new Date(`${first.start_date}T12:00:00`).getTime() + 7 * 86_400_000 : null;
  const nextDeliveryIsStartup = startPlus7 != null && Date.now() < startPlus7;

  return (
    <section className="rounded-xl border border-tcs-border bg-tcs-bg p-4 sm:p-5" aria-label="Dialysis chemicals">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-tcs-text">Dialysis chemicals</h3>
          <p className="mt-0.5 text-xs text-tcs-text-muted">
            {first.display_name}
            {cleanerless ? ' (no cleaner on file)' : ''}. {isNum(first.chair_count) && first.chair_count > 0 ? `${first.chair_count} chair${first.chair_count === 1 ? '' : 's'}` : 'Chairs not on file'},{' '}
            {isNum(first.nights_per_week) ? `cleaned ${first.nights_per_week} nights a week` : 'nights cleaned not on file'}. Supplied from{' '}
            {first.stock_office_id ? officeName[first.stock_office_id] ?? first.stock_office_id : 'no location yet'}
            {first.stock_office_overridden ? ' (set for this clinic)' : ''}. AM: {first.account_manager_name ?? 'none on file'}.
          </p>
          {nd && (
            <p className="mt-1 text-xs font-medium text-st-warn-fg-strong">
              Needs data: {nd}. Excluded from per-chair figures until it is filled in.
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button type="button" className={BTN_PRIMARY} onClick={() => setDeliveryOpen(true)}>
            {firstDelivery ? 'Log first delivery' : 'Log a delivery'}
          </button>
          <button type="button" className={BTN} onClick={() => setCountOpen(true)}>
            {rows.some((r) => r.has_shelf) ? 'Update count' : 'Count what’s there now'}
          </button>
        </div>
      </div>

      <p className="mt-3 text-xs text-tcs-text-secondary">
        {firstDelivery ? (
          <>
            New chemical set: Micro-Chem Plus and Stearns tabs. Awaiting first delivery.
            {phasing.data.length > 0
              ? ` Old stock to pick up on that visit: ${phasing.data.map((p) => p.name).join(', ')}.`
              : ''}{' '}
            SDS sheets get confirmed on that visit.
          </>
        ) : (
          <>
            SDS for both products {first.sds_posted ? 'posted' : 'not yet confirmed as posted'}. Old stock{' '}
            {phasing.data.length === 0 ? 'picked up' : `still to pick up: ${phasing.data.map((p) => p.name).join(', ')}`}.
            {isNum(first.bottles_labeled) ? ` ${first.bottles_labeled} bottles labeled.` : ''}
          </>
        )}
      </p>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {rows.map((r) => (
          <ProductPanel
            key={r.supply_id}
            row={r}
            whyOpen={!!why[r.supply_id ?? '']}
            onToggleWhy={() => setWhy((w) => ({ ...w, [r.supply_id ?? '']: !w[r.supply_id ?? ''] }))}
            onCorrectCount={() =>
              r.last_count_id &&
              setCorrection({
                movementId: r.last_count_id,
                what: `the ${fmtDay(r.last_count_at)} count`,
                currentQty: Number(r.last_count_qty ?? 0),
                unitLabel: r.unit_label,
              })
            }
            onCount={() => setCountOpen(true)}
            onEditRate={() =>
              setSettingTarget({
                kind: 'usage',
                facilityId,
                supplyId: r.supply_id ?? '',
                title: `${first.display_name} — ${r.unit_label === 'tabs' ? 'Stearns tabs' : 'Micro-Chem Plus'}`,
                unitLabel: r.unit_label,
                learnedMonth: r.auto_month,
                current: r.fixed_per_month,
              })
            }
            fixedByName={r.fixed_by_eid ? history.data.names[r.fixed_by_eid]?.name ?? null : null}
          />
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-tcs-text-muted">
        <span>
          Deliver at {first.deliver_at_days} days left, fill to {first.fill_to_days}.{' '}
          <button
            type="button"
            className="underline"
            onClick={() =>
              setSettingTarget({
                kind: 'target',
                scope: 'facility',
                facilityId,
                key: 'deliver_at_days',
                title: first.display_name ?? '',
                current: Number(first.deliver_at_days ?? 7),
                isDefault: Number(first.deliver_at_days) === 7,
              })
            }
          >
            Edit deliver-at
          </button>{' '}
          ·{' '}
          <button
            type="button"
            className="underline"
            onClick={() =>
              setSettingTarget({
                kind: 'target',
                scope: 'facility',
                facilityId,
                key: 'fill_to_days',
                title: first.display_name ?? '',
                current: Number(first.fill_to_days ?? 30),
                isDefault: Number(first.fill_to_days) === 30,
              })
            }
          >
            Edit fill-to
          </button>
        </span>
        <button
          type="button"
          className="underline"
          onClick={() =>
            setSettingTarget({
              kind: 'stock_office',
              facilityId,
              title: first.display_name ?? '',
              current: first.stock_office_id,
              overridden: !!first.stock_office_overridden,
              offices: refs.data.offices,
            })
          }
        >
          Change supplying location
        </button>
        {!firstDelivery && (
          <button type="button" className="underline" onClick={() => setPickupOpen(true)}>
            Pick up leftover stock
          </button>
        )}
      </div>

      <CostSection
        costs={myCosts}
        officeName={first.stock_office_id ? officeName[first.stock_office_id] ?? first.stock_office_id : 'its location'}
        nextIsStartup={nextDeliveryIsStartup}
        chairCount={first.chair_count}
        averagesReady={clinicsWithTwoCounts >= 10}
      />

      <HistorySection
        moves={history.data.moves}
        corrections={history.data.corrections}
        names={history.data.names}
        offices={officeName}
        loading={history.loading}
        error={history.error}
        onEdit={(m) =>
          setCorrection({
            movementId: m.movement_id ?? '',
            what: `the ${m.kind} on ${fmtDay(m.occurred_at)}`,
            currentQty: Number(m.qty ?? 0),
            unitLabel: m.supply_id === 'SUP-0230' ? 'tabs' : 'gal',
          })
        }
      />

      <LogDeliveryWindow open={deliveryOpen} onClose={() => setDeliveryOpen(false)} rows={rows} offices={refs.data.offices} />
      <UpdateCountWindow
        open={countOpen}
        onClose={() => setCountOpen(false)}
        target={{
          facilityId,
          title: first.display_name ?? '',
          amEid: first.account_manager_eid,
          amName: first.account_manager_name,
          providerName: cleanerOf(first.display_name, first.facility_name),
          products: rows.map((r) => ({ supply_id: r.supply_id ?? '', unit_label: r.unit_label })),
        }}
      />
      <CorrectEntryWindow open={!!correction} onClose={() => setCorrection(null)} target={correction} />
      <StockSettingWindow open={!!setting} onClose={() => setSettingTarget(null)} target={setting} />
      <PickupWindow
        open={pickupOpen}
        onClose={() => setPickupOpen(false)}
        facilityId={facilityId}
        title={first.display_name ?? ''}
        offices={refs.data.offices}
        products={refs.data.products}
        defaultOffice={first.stock_office_id}
      />
    </section>
  );
}

function ProductPanel({
  row: r,
  whyOpen,
  onToggleWhy,
  onCorrectCount,
  onCount,
  onEditRate,
  fixedByName,
}: {
  row: ClinicStockRow;
  whyOpen: boolean;
  onToggleWhy: () => void;
  onCorrectCount: () => void;
  onCount: () => void;
  onEditRate: () => void;
  fixedByName: string | null;
}) {
  const name = r.unit_label === 'tabs' ? 'Stearns tabs' : 'Micro-Chem Plus';
  const days = r.days_left;
  const deliverAt = Number(r.deliver_at_days ?? 7);
  const fillTo = Number(r.fill_to_days ?? 30);
  const tone = !isNum(days) ? 'none' : days <= deliverAt ? 'low' : days <= deliverAt * 2 ? 'watch' : 'ok';
  const bar = tone === 'low' ? 'bg-st-danger-solid' : tone === 'watch' ? 'bg-st-warn-solid' : 'bg-st-ok-solid';
  const pct = isNum(days) ? Math.min(100, (days / fillTo) * 100) : 0;
  const markerPct = Math.min(100, (deliverAt / fillTo) * 100);

  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-surface p-3">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-sm font-semibold text-tcs-text">{name}</h4>
        <span
          className={`text-xs font-medium ${
            tone === 'low' ? 'text-st-danger-fg' : tone === 'watch' ? 'text-st-warn-fg' : tone === 'ok' ? 'text-st-ok-fg' : 'text-tcs-text-muted'
          }`}
        >
          {isNum(days) ? `${fmtDays(days)} left` : 'No shelf level yet'}
        </span>
      </div>

      {r.has_shelf ? (
        <>
          <p className="mt-1 text-lg font-semibold text-tcs-text">
            {fmtQty(r.shelf_now, r.unit_label)}
            {r.is_estimated && <span className="ml-2 text-xs font-normal text-tcs-text-muted">estimated</span>}
          </p>
          <div className="relative mt-2 h-2 rounded-full bg-tcs-bg" aria-hidden>
            <div className={`h-2 rounded-full ${bar}`} style={{ width: `${pct}%` }} />
            <div className="absolute -top-1 h-4 w-0.5 bg-tcs-text" style={{ left: `${markerPct}%` }} title={`Deliver at ${deliverAt} days`} />
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-tcs-text-muted">
            <span>Today</span>
            <span>deliver at {deliverAt} days</span>
            <span>{fillTo} days</span>
          </div>
        </>
      ) : (
        <p className="mt-1 text-xs text-tcs-text-secondary">
          The first delivery or count sets it. Expected use: about {fmtRateMonth(r.rate_month, r.unit_label)}
          {r.rate_source === 'estimate' ? ' (starting estimate)' : ''}.
        </p>
      )}

      <div className="mt-2 flex flex-wrap gap-1">
        <span className="rounded-full border border-tcs-border px-2 py-0.5 text-[10px] text-tcs-text-muted">
          {fmtRateMonth(r.rate_month, r.unit_label)} · {rateSourceLabel(r)}
        </span>
        {r.needs_data && (
          <span className="rounded-full border border-st-warn-border bg-st-warn-bg px-2 py-0.5 text-[10px] text-st-warn-fg">needs data</span>
        )}
        {r.check_setting && (
          <span className="rounded-full border border-st-warn-border bg-st-warn-bg px-2 py-0.5 text-[10px] text-st-warn-fg">check your setting</span>
        )}
        {(r.n_negative ?? 0) > 0 && (
          <span className="rounded-full border border-st-danger-border bg-st-danger-bg px-2 py-0.5 text-[10px] text-st-danger-fg">
            a count went up with no delivery — check it
          </span>
        )}
        {r.jump_flag && (
          <span className="rounded-full border border-st-warn-border bg-st-warn-bg px-2 py-0.5 text-[10px] text-st-warn-fg">
            use jumped over 2× — confirm the count
          </span>
        )}
      </div>

      {r.rate_source === 'fixed' && (
        <p className="mt-2 text-xs text-tcs-text-secondary">
          Set by {fixedByName ?? 'someone'} on {fmtDay(r.fixed_at)}: {fmtRateMonth(r.fixed_per_month, r.unit_label)}
          {r.fixed_until ? `, until ${fmtDay(r.fixed_until)}` : ''}.
          {r.fixed_reason ? ` “${r.fixed_reason}”.` : ''}
        </p>
      )}

      <button type="button" onClick={onToggleWhy} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-tcs-text-secondary">
        {whyOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} Why this number?
      </button>
      {whyOpen && (
        <ul className="mt-1 space-y-1 text-xs text-tcs-text-secondary">
          {whyThisNumber(r).map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      )}

      <div className="mt-2 flex flex-wrap gap-1.5">
        {r.last_count_id ? (
          <button type="button" className={BTN} onClick={onCorrectCount}>
            Correct the count
          </button>
        ) : (
          <button type="button" className={BTN} onClick={onCount}>
            Count what’s there
          </button>
        )}
        <button type="button" className={BTN} onClick={onEditRate}>
          Edit usage rate
        </button>
      </div>
    </div>
  );
}

function CostSection({
  costs,
  officeName,
  nextIsStartup,
  chairCount,
  averagesReady,
}: {
  costs: Awaited<ReturnType<typeof fetchDeliveryCosts>>;
  officeName: string;
  nextIsStartup: boolean;
  chairCount: number | null;
  averagesReady: boolean;
}) {
  const operating = costs.filter((c) => !c.is_startup);
  const startup = costs.filter((c) => c.is_startup);
  const unpriced = costs.filter((c) => c.cost == null).length;
  const byMonth = new Map<string, number>();
  for (const c of operating) {
    if (c.cost == null || !c.month) continue;
    byMonth.set(c.month, (byMonth.get(c.month) ?? 0) + Number(c.cost));
  }
  const months = Array.from(byMonth.entries()).sort(([a], [b]) => (a < b ? 1 : -1));
  const thisMonth = new Date().toISOString().slice(0, 7);
  const startupTotal = startup.reduce((s, c) => s + Number(c.cost ?? 0), 0);

  return (
    <div className="mt-4 border-t border-tcs-border pt-3">
      <h4 className="text-sm font-semibold text-tcs-text">Monthly chemical cost</h4>
      {costs.length === 0 ? (
        <p className="mt-1 text-xs text-tcs-text-secondary">
          No cost yet. Cost starts with the first delivery, priced at {officeName}’s average landed cost. A delivery here
          {nextIsStartup ? ' before the start date + 7 days counts as startup cost, not operating cost.' : ' today counts as operating cost, not startup.'}
        </p>
      ) : (
        <>
          <p className="mt-1 text-[11px] text-tcs-text-muted">What was delivered, priced at the location’s average landed cost when it left the shelf.</p>
          {months.length === 0 ? (
            <p className="mt-1 text-xs text-tcs-text-secondary">No operating cost yet.</p>
          ) : (
            <ul className="mt-1 space-y-0.5 text-sm">
              {months.map(([m, v]) => (
                <li key={m} className="flex justify-between gap-2">
                  <span className="text-tcs-text-secondary">
                    {new Date(`${m}T12:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    {m.slice(0, 7) === thisMonth ? ' (partial)' : ''}
                  </span>
                  <span className="font-medium text-tcs-text">{fmtMoney(v)}</span>
                </li>
              ))}
            </ul>
          )}
          {startup.length > 0 && (
            <p className="mt-1 text-xs text-tcs-text-secondary">Startup deliveries: {fmtMoney(startupTotal)} — reported with startup cost.</p>
          )}
          {unpriced > 0 && (
            <p className="mt-1 text-xs text-st-warn-fg">
              {unpriced} deliver{unpriced === 1 ? 'y was' : 'ies were'} made before any purchase was recorded at that location, so
              {unpriced === 1 ? ' it has' : ' they have'} no price yet.
            </p>
          )}
          {months.length > 0 && isNum(chairCount) && chairCount > 0 && averagesReady && (
            <p className="mt-1 text-xs text-tcs-text-muted">
              This clinic: {fmtMoney(months[0][1] / chairCount)} per chair for the latest month.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function HistorySection({
  moves,
  corrections,
  names,
  offices,
  loading,
  error,
  onEdit,
}: {
  moves: MovementRow[];
  corrections: CorrectionRow[];
  names: Record<string, { name: string; role: string | null }>;
  offices: Record<string, string>;
  loading: boolean;
  error: string | null;
  onEdit: (m: MovementRow) => void;
}) {
  const byTarget = new Map<string, CorrectionRow[]>();
  for (const c of corrections) {
    const k = c.corrects_movement_id ?? '';
    byTarget.set(k, [...(byTarget.get(k) ?? []), c]);
  }
  return (
    <div className="mt-4 border-t border-tcs-border pt-3">
      <h4 className="text-sm font-semibold text-tcs-text">Deliveries, counts and corrections</h4>
      {error && <p className="mt-1 text-xs text-st-danger-fg">Could not load the history: {error}</p>}
      {!loading && !error && moves.length === 0 && (
        <p className="mt-1 text-xs text-tcs-text-secondary">
          Nothing logged yet. Deliveries, counts, night-manager reports and corrections will appear here, each with who entered it.
        </p>
      )}
      {moves.length > 0 && (
        <ul className="mt-1 divide-y divide-tcs-border">
          {moves.map((m) => {
            const cs = byTarget.get(m.movement_id ?? '') ?? [];
            return (
              <li key={m.movement_id} className="flex items-start justify-between gap-2 py-2">
                <div className="min-w-0 text-xs">
                  <p className="text-tcs-text">
                    <span className="mr-2 text-tcs-text-muted">{fmtDay(m.occurred_at)}</span>
                    {movementPhrase(m, names, offices)}
                    {m.kind === 'delivery' && m.sds_posted ? ' · SDS posted' : ''}
                    {m.kind === 'delivery' && m.old_chemicals_picked_up ? ' · old chemicals picked up' : ''}
                    {m.kind === 'delivery' && m.day_stop_id ? ' · route stop checked off' : ''}
                  </p>
                  {cs.map((c, i) => {
                    const before = i === 0 ? m.original_qty : cs[i - 1].qty;
                    const u = m.supply_id === 'SUP-0230' ? 'tabs' : 'gal';
                    return (
                      <p key={c.movement_id} className="mt-0.5 text-tcs-text-secondary">
                        Corrected by {c.recorded_by_eid ? names[c.recorded_by_eid]?.name ?? c.recorded_by_eid : 'someone'} on{' '}
                        {fmtDay(c.recorded_at)}: {fmtQty(before, u)} → {fmtQty(c.qty, u)} — {c.reason}. Usage and days left recalculated.
                      </p>
                    );
                  })}
                  {m.notes && <p className="mt-0.5 text-tcs-text-muted">{m.notes}</p>}
                </div>
                <button type="button" className={BTN} onClick={() => onEdit(m)}>
                  Edit
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default DialysisStockCard;
